import { maxFilesForField, validateAttachmentDescriptors } from "./attachments";
import { isMarketingConsentTruthy } from "./consent-truthy";
import {
	collectNamedFormElements,
	type FormElementNode,
} from "./form-schema-walk";

export type FormValidationResult =
	| { errors: Record<string, string>; valid: false }
	| { valid: true; values: Record<string, unknown> };

export interface ValidateFormSubmissionOptions {
	/** Max length for string values after trim (default 10_000). */
	maxStringLength?: number;
}

const DEFAULT_MAX_STRING_LENGTH = 10_000;
const MAX_PERSON_NAME_LENGTH = 100;

const PERSON_NAME_KEYS = new Set([
	"firstname",
	"lastname",
	"first_name",
	"last_name",
	"firstName",
	"lastName",
]);

/** Letters, marks, spaces, hyphens, apostrophes — common person-name characters. */
const PERSON_NAME_PATTERN = /^[\p{L}\p{M}'\-\s]+$/u;

const EMAIL_PATTERN =
	/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const PHONE_PATTERN = /^\+?[\d\s().-]+$/;

function stripC0ControlChars(s: string): string {
	let out = "";
	for (const ch of s) {
		const c = ch.codePointAt(0) ?? 0;
		if (c <= 8 || (c >= 11 && c <= 31)) {
			continue;
		}
		out += ch;
	}
	return out;
}

function sanitizeSubmittedValue(v: unknown, maxStringLength: number): unknown {
	if (typeof v === "string") {
		const cleaned = stripC0ControlChars(v).trim();
		return cleaned.length > maxStringLength
			? cleaned.slice(0, maxStringLength)
			: cleaned;
	}
	if (Array.isArray(v)) {
		return v
			.slice(0, 200)
			.map((item) => sanitizeSubmittedValue(item, maxStringLength));
	}
	return v;
}

function isEmptyValue(v: unknown): boolean {
	if (v == null || v === false) {
		return true;
	}
	if (typeof v === "string" && v.trim() === "") {
		return true;
	}
	if (Array.isArray(v) && v.length === 0) {
		return true;
	}
	return false;
}

function isPersonNameField(name: string): boolean {
	return PERSON_NAME_KEYS.has(name) || PERSON_NAME_KEYS.has(name.toLowerCase());
}

function usesPresetValue(node: FormElementNode): boolean {
	return (
		node.type === "form_field" &&
		(node.hidden === true || node.fieldType === "hidden")
	);
}

function resolveRawValue(
	node: FormElementNode,
	submittedData: Record<string, unknown>
): unknown {
	if (!node.name) {
		return undefined;
	}
	if (usesPresetValue(node)) {
		return node.presetValue ?? "";
	}
	return submittedData[node.name];
}

function optionValueSet(
	options: FormElementNode["options"]
): Set<string> | null {
	if (!options?.length) {
		return null;
	}
	return new Set(options.map((o) => o.value));
}

function validatePersonName(value: unknown, required: boolean): string | null {
	if (typeof value !== "string") {
		return required ? "This field is required." : "Please enter a valid name.";
	}
	const t = value.trim();
	if (!t) {
		return required ? "This field is required." : null;
	}
	if (t.length > MAX_PERSON_NAME_LENGTH) {
		return `Must be at most ${MAX_PERSON_NAME_LENGTH} characters.`;
	}
	if (!PERSON_NAME_PATTERN.test(t)) {
		return "Please enter a valid name.";
	}
	return null;
}

function validateEmailValue(value: unknown, required: boolean): string | null {
	if (isEmptyValue(value)) {
		return required ? "This field is required." : null;
	}
	if (typeof value !== "string") {
		return "Please enter a valid email address.";
	}
	const t = value.trim();
	if (!EMAIL_PATTERN.test(t)) {
		return "Please enter a valid email address.";
	}
	return null;
}

function validatePhoneValue(value: unknown, required: boolean): string | null {
	if (isEmptyValue(value)) {
		return required ? "This field is required." : null;
	}
	if (typeof value !== "string") {
		return "Please enter a valid phone number.";
	}
	const t = value.trim();
	if (!PHONE_PATTERN.test(t)) {
		return "Please enter a valid phone number.";
	}
	const digits = t.replace(/\D/g, "");
	if (digits.length < 7 || digits.length > 15) {
		return "Please enter a valid phone number.";
	}
	return null;
}

function validateSelectValue(
	value: unknown,
	required: boolean,
	options: FormElementNode["options"]
): string | null {
	if (isEmptyValue(value)) {
		return required ? "This field is required." : null;
	}
	if (typeof value !== "string") {
		return "Please select a valid option.";
	}
	const allowed = optionValueSet(options);
	if (allowed && !allowed.has(value)) {
		return "Please select a valid option.";
	}
	return null;
}

function validateRadioValue(
	value: unknown,
	required: boolean,
	options: FormElementNode["options"]
): string | null {
	if (isEmptyValue(value)) {
		return required ? "This field is required." : null;
	}
	if (typeof value !== "string") {
		return "Please select a valid option.";
	}
	const allowed = optionValueSet(options);
	if (allowed && !allowed.has(value)) {
		return "Please select a valid option.";
	}
	if (allowed && allowed.size === 0) {
		return "Please select a valid option.";
	}
	return null;
}

function validateCheckboxValue(
	value: unknown,
	required: boolean,
	options: FormElementNode["options"]
): string | null {
	const allowed = optionValueSet(options);
	if (allowed && allowed.size > 0) {
		if (isEmptyValue(value)) {
			return required ? "This field is required." : null;
		}
		const items = Array.isArray(value) ? value : [value];
		for (const item of items) {
			if (typeof item !== "string" || !allowed.has(item)) {
				return "Please select valid option(s).";
			}
		}
		return null;
	}
	if (required && !isMarketingConsentTruthy(value)) {
		return "This field is required.";
	}
	return null;
}

function validateConsentValue(
	value: unknown,
	required: boolean
): string | null {
	if (required && !isMarketingConsentTruthy(value)) {
		return "Consent is required.";
	}
	return null;
}

function valueAsString(value: unknown): string {
	if (typeof value === "string") {
		return value;
	}
	if (value == null) {
		return "";
	}
	return String(value);
}

function validateSchemaPattern(
	asString: string,
	pattern: string | undefined
): string | null {
	if (!pattern) {
		return null;
	}
	try {
		if (!new RegExp(pattern).test(asString)) {
			return "Invalid format.";
		}
	} catch {
		/* ignore invalid schema pattern */
	}
	return null;
}

function validateSchemaNumberRange(
	value: unknown,
	validation: NonNullable<FormElementNode["validation"]>
): string | null {
	if (validation.min == null && validation.max == null) {
		return null;
	}
	if (isEmptyValue(value)) {
		return null;
	}
	const n =
		typeof value === "number"
			? value
			: Number(typeof value === "string" ? value.trim() : value);
	if (!Number.isFinite(n)) {
		return "Please enter a valid number.";
	}
	if (validation.min != null && n < validation.min) {
		return `Must be at least ${validation.min}.`;
	}
	if (validation.max != null && n > validation.max) {
		return `Must be at most ${validation.max}.`;
	}
	return null;
}

function applySchemaValidation(
	value: unknown,
	validation: FormElementNode["validation"] | undefined,
	fieldType: string | undefined
): string | null {
	if (!validation) {
		return null;
	}
	const asString = valueAsString(value);

	if (validation.maxLength != null && asString.length > validation.maxLength) {
		return `Must be at most ${validation.maxLength} characters.`;
	}

	const patternErr = validateSchemaPattern(asString, validation.pattern);
	if (patternErr) {
		return patternErr;
	}

	if (fieldType === "number") {
		return validateSchemaNumberRange(value, validation);
	}

	return null;
}

const FIELD_TYPE_VALIDATORS: Record<
	string,
	(node: FormElementNode, value: unknown, required: boolean) => string | null
> = {
	checkbox: (node, value, required) =>
		validateCheckboxValue(value, required, node.options),
	email: (_node, value, required) => validateEmailValue(value, required),
	file: (node, value, required) =>
		validateAttachmentDescriptors(
			value,
			required,
			maxFilesForField(node.validation?.max)
		),
	radio: (node, value, required) =>
		validateRadioValue(value, required, node.options),
	select: (node, value, required) =>
		validateSelectValue(value, required, node.options),
	tel: (_node, value, required) => validatePhoneValue(value, required),
};

function validateFormFieldNode(
	node: FormElementNode,
	value: unknown
): string | null {
	const required = node.required === true;
	const fieldType = node.fieldType;

	if (fieldType && FIELD_TYPE_VALIDATORS[fieldType]) {
		const err = FIELD_TYPE_VALIDATORS[fieldType](node, value, required);
		if (err) {
			return err;
		}
		// For files `validation.max` is the file count; pattern/maxLength don't apply.
		if (fieldType === "file") {
			return null;
		}
	} else if (
		fieldType === "text" &&
		node.name &&
		isPersonNameField(node.name)
	) {
		const err = validatePersonName(value, required);
		if (err) {
			return err;
		}
	} else if (required && isEmptyValue(value)) {
		return "This field is required.";
	}

	return applySchemaValidation(value, node.validation, fieldType);
}

function validateNode(node: FormElementNode, value: unknown): string | null {
	if (node.type === "form_consent") {
		return validateConsentValue(value, node.required === true);
	}
	if (node.type === "form_field") {
		return validateFormFieldNode(node, value);
	}
	return null;
}

function shouldIncludeInOutput(value: unknown): boolean {
	if (value === undefined) {
		return false;
	}
	if (typeof value === "string" && value === "" && !Array.isArray(value)) {
		return false;
	}
	return true;
}

/**
 * Validates a public form submission against the published Theme Builder schema.
 * Hidden / preset fields ignore client values and use `presetValue` from the schema.
 */
export function validateFormSubmission(
	schemaRoot: unknown,
	submittedData: Record<string, unknown>,
	options?: ValidateFormSubmissionOptions
): FormValidationResult {
	const maxStringLength = options?.maxStringLength ?? DEFAULT_MAX_STRING_LENGTH;
	const fieldDefs = collectNamedFormElements(schemaRoot);
	const errors: Record<string, string> = {};
	const values: Record<string, unknown> = {};

	for (const node of fieldDefs) {
		if (!node.name) {
			continue;
		}
		const raw = resolveRawValue(node, submittedData);
		const sanitized = sanitizeSubmittedValue(raw, maxStringLength);
		const error = validateNode(node, sanitized);
		if (error) {
			errors[node.name] = error;
			continue;
		}
		if (shouldIncludeInOutput(sanitized)) {
			values[node.name] = sanitized;
		}
	}

	if (Object.keys(errors).length > 0) {
		return { valid: false, errors };
	}
	return { valid: true, values };
}

/** Exported for unit tests and downstream reuse. */
export const formValidationPatterns = {
	EMAIL_PATTERN,
	PERSON_NAME_PATTERN,
	PHONE_PATTERN,
} as const;
